/**
 * Who may use this dataset (specs/access-model.md): people with manage /
 * contribute / view, added by email, changed by level, removed. Shown only to
 * those the server lets list the grants (the dataset's managers, usermanagers,
 * admins) — everyone else gets a 403 on the first load and sees nothing.
 * An outside contributor is a person with a contribute grant; datasets have
 * no token links (#799). The page's own address is what to send them: opened
 * without a session it asks for a sign-in, an email code for outsiders.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { KeyRound, Copy } from 'lucide-react';
import { Input, Select, Label, Button, StatusLine } from '../../../components/InstanceSettings/formPrimitives';
import DeleteConfirmButton from '../../../components/DeleteConfirmButton';
import { useTranslation } from '../../../hooks/useTranslation.jsx';
import { listAccess, grantDatasetAccess, revokeDatasetAccess } from './datasetApi';

const ACTIONS = ['manage', 'contribute', 'view'];

export default function DatasetAccess({ datasetId }) {
  const { t } = useTranslation();
  const [rows, setRows] = useState(null); // null = loading or not allowed
  const [email, setEmail] = useState('');
  const [action, setAction] = useState('contribute');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const shareUrl = `${window.location.origin}/admin/knowledge?dataset=${encodeURIComponent(datasetId)}`;

  const load = useCallback(async () => {
    try {
      setRows(await listAccess(datasetId));
      setError(null);
    } catch (err) {
      // 403: not this dataset's manager — the section stays hidden.
      if (err.status !== 403) setError(err.message);
      setRows(null);
    }
  }, [datasetId]);

  useEffect(() => { load(); }, [load]);

  if (rows === null && !error) return null;

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await grantDatasetAccess(datasetId, email.trim(), action);
      setEmail('');
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const change = async (row, next) => {
    setError(null);
    try {
      await grantDatasetAccess(datasetId, row.email, next);
      await load();
    } catch (err) {
      setError(err.message);
    }
  };

  const remove = async (row) => {
    setError(null);
    try {
      await revokeDatasetAccess(datasetId, row.user_id);
      await load();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div style={{ marginTop: 28, paddingTop: 16, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#fff', fontSize: 15, fontWeight: 600 }}>
        <KeyRound size={16} /> {t('datasetWorkspace.access.title')}
      </div>
      <p style={{ color: '#999', fontSize: 13, margin: '6px 0 12px' }}>{t('datasetWorkspace.access.lead')}</p>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#bbb', fontSize: 13, margin: '0 0 12px', flexWrap: 'wrap' }}>
        <span>{t('datasetWorkspace.access.shareLabel')}</span>
        <code style={{ userSelect: 'all', wordBreak: 'break-all' }}>{shareUrl}</code>
        <Button type="button" $size="small" $variant="ghost" title={t('datasetWorkspace.access.copy')}
          onClick={() => navigator.clipboard?.writeText(shareUrl).then(() => setCopied(true)).catch(() => {})}>
          <Copy size={13} /> {copied ? t('datasetWorkspace.access.copied') : t('datasetWorkspace.access.copy')}
        </Button>
      </div>

      {(rows || []).length === 0 && (
        <p style={{ color: '#888', fontSize: 13 }}>{t('datasetWorkspace.access.empty')}</p>
      )}
      {(rows || []).map((row) => (
        <div key={row.user_id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', color: '#ddd', fontSize: 14 }}>
          <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {row.name ? `${row.name} — ${row.email}` : row.email}
          </span>
          <Select value={row.action} onChange={(e) => change(row, e.target.value)} style={{ width: 'auto' }} aria-label={t('datasetWorkspace.access.levelLabel')}>
            {ACTIONS.map((a) => <option key={a} value={a}>{t(`datasetWorkspace.access.actions.${a}`)}</option>)}
          </Select>
          <DeleteConfirmButton onDelete={() => remove(row)} label={t('datasetWorkspace.access.remove')} />
        </div>
      ))}

      <form onSubmit={add} style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginTop: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 220 }}>
          <Label htmlFor="dsa-email">{t('datasetWorkspace.access.emailLabel')}</Label>
          <Input id="dsa-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder={t('datasetWorkspace.access.emailPlaceholder')} required />
        </div>
        <Select value={action} onChange={(e) => setAction(e.target.value)} style={{ width: 'auto' }} aria-label={t('datasetWorkspace.access.levelLabel')}>
          {ACTIONS.map((a) => <option key={a} value={a}>{t(`datasetWorkspace.access.actions.${a}`)}</option>)}
        </Select>
        <Button type="submit" $size="small" $variant="primary" disabled={busy || !email.trim()}>
          {t('datasetWorkspace.access.add')}
        </Button>
      </form>

      {error && <StatusLine $error>{error}</StatusLine>}
    </div>
  );
}
